import { useMemo } from 'react';
import { tryParseJSON } from '@adv-frontend/yastore-modules/lib';
import {
    type OpenApiGetProductVariantsByRequestRequest,
    type OpenApiSchemaABCCombinedCategory,
    type OpenApiSchemaCategory,
    type OpenApiSchemaCollection,
    type OpenApiSchemaProductVariant,
    type OpenApiSchemaProductVariantRequestFilter,
    type OpenApiSchemaYmlGoodsModerationStatus,
} from '#shared/api/schema';
import { mapProductVariantFiltersToRequest, type ProductVariantSortByKey } from '../api';

const joinCollectionSlugs = (
    collectionSlugs: OpenApiSchemaCollection['slug'][] | undefined,
    includeCollectionSlugs: OpenApiSchemaCollection['slug'][] | undefined
): string[] | undefined => {
    if (
        (!collectionSlugs || collectionSlugs.length === 0) &&
        (!includeCollectionSlugs || includeCollectionSlugs.length === 0)
    ) {
        return undefined;
    }

    const joinedCollectionSlugs: string[] = [];

    if (collectionSlugs?.length) {
        joinedCollectionSlugs.push(...collectionSlugs);
    }

    if (includeCollectionSlugs?.length) {
        joinedCollectionSlugs.push(...includeCollectionSlugs);
    }

    return joinedCollectionSlugs;
};

export const useProductVariantsAndCardsQuery = ({
    categorySlugIds = '',
    page,
    perPage,
    searchText,
    statuses,
    filter,
    sortBy,
    sortOrder,
    isSortByKey,
    includeCollectionSlugs,
    excludeCollectionSlugs,
    collectionSlugs,
    product_list_id,
    exclude_product_list_id,
    forceRefetch,
    similarCardId,
    excludeSimilarCardId,
    excludeProductCardIds,
    categorySlugs,
    abcCategories: forceAbcCategories,
    moderationStatus,
}: {
    // Перечисление 'slug' выбранных категорий через запятую
    categorySlugIds?: string;
    page: number;
    perPage: number;
    searchText: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    statuses: OpenApiSchemaProductVariant['status'][];
    // Stringify-JSON набор продвинутых фильтров для грида
    filter?: string;
    isSortByKey: (key: string) => key is ProductVariantSortByKey;
    includeCollectionSlugs?: OpenApiSchemaCollection['slug'][];
    excludeCollectionSlugs?: OpenApiSchemaCollection['slug'][];
    collectionSlugs?: OpenApiSchemaCollection['slug'][];
    product_list_id?: string;
    exclude_product_list_id?: string;
    forceRefetch?: boolean;
    similarCardId?: string;
    excludeSimilarCardId?: string;
    excludeProductCardIds?: string[];
    categorySlugs?: OpenApiSchemaCategory['slug'][];
    abcCategories?: OpenApiSchemaABCCombinedCategory[];
    moderationStatus?: OpenApiSchemaYmlGoodsModerationStatus[];
}) => {
    const categorySlugsArray = useMemo(
        () => [...categorySlugIds.split(','), ...(categorySlugs ?? [])].filter(Boolean),
        [categorySlugIds, categorySlugs]
    );

    const collectionSlugsQuery = useMemo(
        () => joinCollectionSlugs(collectionSlugs, includeCollectionSlugs),
        [collectionSlugs, includeCollectionSlugs]
    );

    const query = useMemo<{ payload: OpenApiGetProductVariantsByRequestRequest; forceRefetch?: boolean }>(() => {
        // Исключаем из общего фильтра фильтры, которые отправляются отдельными свойствами
        const filtersRequest = mapProductVariantFiltersToRequest(
            tryParseJSON<OpenApiSchemaProductVariantRequestFilter[]>(filter)
        );

        return {
            payload: {
                page,
                per_page: perPage,
                characteristics: [],
                category_slugs: categorySlugsArray,
                search_text: searchText,
                sort_by: isSortByKey(sortBy) ? sortBy : 'created',
                sort_order: sortOrder,
                statuses,
                collection_slugs: collectionSlugsQuery,
                exclude_collection_slugs: excludeCollectionSlugs,
                similar_card_id: similarCardId,
                exclude_similar_card_id: excludeSimilarCardId,
                exclude_product_card_ids: excludeProductCardIds,
                product_list_id,
                exclude_product_list_id,
                yml_goods_moderation_statuses: moderationStatus,
                ...filtersRequest,
                abc_categories: forceAbcCategories?.length ? forceAbcCategories : filtersRequest.abc_categories,
            },
            forceRefetch,
        };
    }, [
        filter,
        page,
        perPage,
        categorySlugsArray,
        searchText,
        isSortByKey,
        sortBy,
        sortOrder,
        statuses,
        collectionSlugsQuery,
        excludeCollectionSlugs,
        similarCardId,
        excludeSimilarCardId,
        excludeProductCardIds,
        product_list_id,
        exclude_product_list_id,
        moderationStatus,
        forceAbcCategories,
        forceRefetch,
    ]);

    return query;
};
